import { FormatDate } from '@/components/PrettyDate';
import styles from '@/components/ProjectDetails/Cards/Items/index.less';
import ServiceRequestAction from '@/components/Lead/ServiceRequestAction';
import { Space } from 'antd';
import classNames from 'classnames';
import { ServiceRequestModel } from '@/typings/models/ServiceRequest';
import { useDispatch } from 'umi';
import AppointmentStatusBadge from '@/components/ProjectDetails/Cards/Items/AppointmentStatusBadge';
import DateIcon from '@/components/DateIcon';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { ClockCircleOutlined, EnvironmentOutlined, IdcardOutlined } from '@ant-design/icons';
import ServiceRequestContact from '@/components/Kanban/ServiceRequests/ServiceRequestContact';
import { getAppointmentContactToShow } from '@/utils/contacts';
import { F } from '@/utils/intl';
import { useOverflowTooltip } from '@/utils/hooks';
import { getServiceRequestDisplayDate, isCallbackFromLocation } from '@/utils/serviceRequest';

export default function AppointmentItem({
  appointment,
  refresh,
  lead,
  fadePastEvent,
  nextAction,
  highlighted,
}: {
  appointment: ServiceRequestModel;
  lead: LeadDetailsModel;
  nextAction?: boolean;
  refresh: () => void;
  fadePastEvent?: boolean;
  highlighted?: boolean;
}) {
  const dispatch = useDispatch();

  const titleText = appointment.title || appointment.branch_service?.service_type?.name;
  // Left out when it's the private customer themself: the project already names them.
  const appointmentContact = getAppointmentContactToShow({ contact: appointment.contact, lead: appointment.lead || lead });
  const titleNode = useOverflowTooltip(titleText);

  const getTitle = () => {
    if (appointment.blocker)
      return (
        <div
          className={classNames(
            styles.calendarEventTitle,
            styles.projectDetailItemTitle,
          )}
        >
          {titleNode}
        </div>
      );
    return (
      <Space size={4} className={styles.projectDetailItemTitle}>
        <AppointmentStatusBadge status={appointment.status} />
        {titleNode}
      </Space>
    );
  };

  const handleAppointmentClick = () => {
    if (appointment.blocker)
      return dispatch({
        type: 'serviceRequest/toggleCalendarEventDetailsDrawer',
        payload: { calendarEventId: appointment.id },
      });
    dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
      payload: { appointmentId: appointment.id },
    });
  };

  const isCallback = isCallbackFromLocation(appointment);
  const date = getServiceRequestDisplayDate(appointment);
  const isPast = !isCallback && new Date(appointment.utc_start_time) < new Date();

  return (
    <div
      onClick={handleAppointmentClick}
      role="item-container"
      data-testid="project-appointment-item"
      style={appointment.blocker && appointment.color ? { '--event-color': appointment.color } : undefined}
      className={classNames(
        styles.projectDetailItemContainer,
        styles.appointmentItemContainer,
        styles.projectDetailItemContainerClickable,
        {
          [styles.calendarEventItem]: appointment.blocker && !nextAction,
          [styles.pastAppointmentItemContainer]: fadePastEvent && isPast,
          [styles.highlightedItemContainer]: highlighted,
          [styles.nextActionItemContainer]: nextAction,
        },
      )}
    >
      {nextAction ? (
        <div className={styles.projectDetailItemContent}>
          <div className={styles.nextActionItemIcon}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div className={styles.projectDetailItemHeader}>
              <div className={styles.nextActionTitle}>
                <F id="pages.lead.next_action" defaultMessage="Next Action" />
              </div>
              {getTitle()}
            </div>
            <div className={styles.projectDetailItemBody}>
              <Space size={6}>
                <DateIcon date={date} />
                <FormatDate date={date} appointmentDate={!isCallback} />
              </Space>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.appointmentCardTimeCol}>
            <span className={styles.appointmentCardDate}>
              <FormatDate format="D. MMM" date={date} />
            </span>
            <span className={styles.appointmentCardTime}>
              {isCallback ? (
                <F id="pages.lead.activities.callback" defaultMessage="Callback" />
              ) : (
                <FormatDate date={date} onlyTime />
              )}
            </span>
          </div>
          <div className={styles.projectDetailItemContent}>
            <div className={styles.projectDetailItemHeader}>{getTitle()}</div>
            {appointment.branch_service?.branch?.name && (
              <div className={styles.appointmentCardMeta}>
                <EnvironmentOutlined />
                <span>{appointment.branch_service.branch.name}</span>
              </div>
            )}
            {appointmentContact && (
              <div className={styles.appointmentCardMeta}>
                <IdcardOutlined />
                <ServiceRequestContact
                  variant="compact"
                  contact={appointmentContact}
                  leadId={lead?.id}
                  customerId={lead?.customer?.id}
                />
              </div>
            )}
          </div>
        </>
      )}
      <div className={styles.projectDetailItemActions}>
        <ServiceRequestAction
          serviceRequest={appointment}
          lead={appointment.lead || lead}
          onActionEnd={refresh}
          allActionsInDropdown
        />
      </div>
    </div>
  );
}
